import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, ConvertPurchaseOrderVerify, ConvertPurchaseOrderToPurchase } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { usePermissions } from '../../../usePermissions';
import {
  Add, Edit, Visibility, Delete, ShoppingCart, Warning, CheckCircle, Cancel, Close, ExpandMore, ExpandLess, Print, Search, PictureAsPdf, WhatsApp
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Collapse,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery, useTheme
} from '@mui/material';
import PurchaseOrderModal from './PurchaseOrderModal';
import BpsGlobalLogo from '../Logo/BpsGlobal.jpeg';
import { downloadInternationalPOPdf, shareInternationalPOViaWhatsApp } from './InternationalPOPdfGenerator';
import { downloadLocalPOPdf, shareLocalPOViaWhatsApp } from './LocalPOPdfGenerator';
import './PurchaseOrderIndex.css';

const PurchaseOrderIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, userId, permissions, companyName } = useAuth();
  const { canCreateTransaction, canEditTransaction, canDeleteTransaction } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Search expand state for mobile
  const [searchExpanded, setSearchExpanded] = useState(false);

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);

  // Supplier drill-down modal states
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPurchaseOrders, setSupplierPurchaseOrders] = useState([]);
  const [expandedPOId, setExpandedPOId] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVerified, setFilterVerified] = useState('all');

  // Debounced search to prevent excessive filtering
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Default fromDate to first day of current month, toDate to today
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getDefaultToDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [orderDetails, setOrderDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');



  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);


  // Fetch functions
  const fetchPurchaseOrders = async (from = fromDate, to = toDate) => {
    try {
      const activeResponse = await fetchData('PurchaseOrderApi', `/${companyId}/${branchId}/${from}/${to}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: true }));
      setPurchaseOrders(activeData);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      setPurchaseOrders([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      const suppliersData = response.response_data || response || [];
      console.log('Fetched Suppliers:', suppliersData);
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  /**
   * Fetch items with optional search functionality - Optimized with debouncing
   * @param {string} inputValue - Optional search term to filter items by name
   */
  const fetchItems = useCallback(async (inputValue = '') => {
    try {
      // Construct API path with optional search parameter
      // If inputValue is provided, use the search API: /Item/{companyId}/{branchId}/{searchTerm}
      // Otherwise, use the default API: /Item/{companyId}
      const apiPath = inputValue
        ? `/${companyId}/${branchId}/${encodeURIComponent(inputValue)}`
        : `/${companyId}`;

      const response = await fetchData('ItemApi', apiPath, token);
      const newItems = response.response_data || response || [];

      // Only update if items actually changed to prevent unnecessary re-renders
      setItems(prevItems => {
        if (JSON.stringify(prevItems) !== JSON.stringify(newItems)) {
          return newItems;
        }
        return prevItems;
      });
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  }, [companyId, branchId, token]);

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      console.log('MaxBillNo API Response:', response);
      const data = response.response_data || response || {};
      console.log('MaxBillNo Data:', data);

      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      // Generate PurchaseOrderNo based on available data
      let purchaseOrderNo = '';
      if (billPrefix && finYear) {
        purchaseOrderNo = `${billPrefix}/${billNumber}/${finYear}`;
      } else if (finYear) {
        purchaseOrderNo = `${billNumber}/${finYear}`;
      } else if (billPrefix) {
        purchaseOrderNo = `${billPrefix}/${billNumber}`;
      } else {
        purchaseOrderNo = `${billNumber}`;
      }

      return { billNumber, billPrefix, finYear, purchaseOrderNo };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '', purchaseOrderNo: '1' };
    }
  };

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Freight + Packing GST
  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      console.log('AccountHeadFreightAndPacking API Response:', response);
      const data = response.response_data || response || [];
      console.log('AccountHeadFreightAndPacking Data:', data);

      // Find Freight and Packing entries from the response
      let freightData = null;
      let packingData = null;

      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight') || item.AccountHeadName?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing') || item.AccountHeadName?.toLowerCase().includes('packing'));
      } else if (data) {
        // If it's an object with Freight and Packing properties
        freightData = data.Freight || data.FreightCharge || null;
        packingData = data.Packing || data.PackingCharge || null;
      }

      setFreightPackingGST({
        freight: freightData,
        packing: packingData
      });

      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };


  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchase orders...');
    await Promise.allSettled([fetchPurchaseOrders(), fetchSuppliers(), fetchItems(), fetchFreightAndPackingGST()]);
    setLoading(false);
  }, [fetchItems]);

  const fetchSinglePurchaseOrder = async (id) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/${id}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
      return null;
    }
  };





  // Fetch PO history for modal (when supplier is selected in create mode)
  const fetchPOHistoryForModal = async (supplierId) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/PurchaseOrderHistoryByMaster/${supplierId}/${branchId}`, token);
      const responseData = response.response_data || response || [];

      let purchaseOrdersList = [];
      let purchaseOrderDetailsList = [];

      if (Array.isArray(responseData) && responseData.length > 0) {
        const firstItem = responseData[0];
        purchaseOrdersList = firstItem.PurchaseOrder || firstItem.purchaseOrder || [];
        purchaseOrderDetailsList = firstItem.PurchaseOrderDetails || firstItem.purchaseOrderDetails || [];
      } else if (responseData.PurchaseOrder) {
        purchaseOrdersList = responseData.PurchaseOrder || [];
        purchaseOrderDetailsList = responseData.PurchaseOrderDetails || [];
      }

      // Map details to each purchase order
      const purchaseOrdersWithDetails = purchaseOrdersList.map(po => {
        const details = purchaseOrderDetailsList.filter(detail => detail.PurchaseOrderId === po.Id);
        return { ...po, PurchaseOrderDetails: details };
      });

      return purchaseOrdersWithDetails;
    } catch (error) {
      console.error('Error fetching PO history for modal:', error);
      return [];
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setLoading(true);
      setLoadingMessage('Fetching purchase order details...');

      const freshData = await fetchSinglePurchaseOrder(itemId);

      if (freshData) {
        const poData = freshData.PurchaseOrder || freshData;
        const details = freshData.PurchaseOrderDetails || freshData.Details || [];
        const termsDetails = freshData.PurchaseTermsDetails || [];

        // Get supplier name from MasterName or find from suppliers list
        const supplierName = poData.MasterName || suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId || (s.MasterId || s.Id) === poData.SupplierId)?.Name || '';

        // Get InvoiceType name from suppliers list
        const supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId || (s.MasterId || s.Id) === poData.SupplierId);
        const invoiceTypeName = poData.InvoiceTypeName || supplier?.InvoiceTypeName || supplier?.InvoiceType || '';

        setSelectedItem(poData);
        setFormData({
          Id: poData.Id,
          CompanyId: poData.CompanyId || companyId,
          BranchId: poData.BranchId || 0,
          SupplierId: poData.MasterId || poData.SupplierId,
          MasterName: supplierName,
          InvoiceTypeId: poData.InvoiceTypeId || 0,
          InvoiceTypeName: invoiceTypeName,
          CurrencyTypeId: poData.CurrencyTypeId || supplier?.CurrencyTypeId || null,
          CurrencyName: poData.CurrencyName || supplier?.CurrencyName || '',
          PurchaseOrderNo: poData.PurchaseOrderNo,
          PurchaseOrderDate: poData.PurchaseOrderDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          FinYear: poData.FinYear || '',
          Instruction1: poData.Instruction1 || '',
          Instruction2: poData.Instruction2 || '',
          Remarks: poData.Remarks || '',
          Amount: poData.Amount || poData.amount || 0,
          DiscountAmount: poData.DiscountAmount || poData.discountAmount || 0,
          TaxableAmount: poData.TaxableAmount || poData.taxableAmount || 0,
          VATAmount: poData.VATAmount || poData.vatAmount || 0,
          FreightAmount: poData.FreightAmount || poData.freightAmount || 0,
          FreightVATRate: poData.FreightVATRate || poData.freightVATRate || 0,
          FreightVATAmount: poData.FreightVATAmount || poData.freightVATAmount || 0,
          PackingAmount: poData.PackingAmount || poData.packingAmount || 0,
          PackingVATRate: poData.PackingVATRate || poData.packingVATRate || 0,
          PackingVATAmount: poData.PackingVATAmount || poData.packingVATAmount || 0,
          RoundOff: poData.RoundOff || poData.roundOff || 0,
          TotalAmount: poData.TotalAmount || poData.totalAmount || 0,
          PaidAmount: poData.PaidAmount || poData.paidAmount || 0,
          BalanceAmount: poData.BalanceAmount || poData.balanceAmount || 0,
          IsConverted: poData.IsConverted || false,
          IsVerified: poData.IsVerified || false,
          IsActive: poData.IsActive ?? true,
          // Audit fields
          CreatedBy: poData.CreatedBy || null,
          CreatedByName: poData.CreatedByName || '',
          CreatedDate: poData.CreatedDate || poData.CreatedOn || poData.CreatedAt || '',
          ModifiedByName: poData.ModifiedByName || poData.ModifiedBy || poData.UpdatedBy || '',
          ModifiedDate: poData.ModifiedDate || poData.ModifiedOn || poData.UpdatedOn || poData.UpdatedAt || '',
          // Terms & Conditions
          SelectedTerms: termsDetails.map(t => t.TermsAndConditionId || t.Id),
          SelectedTermsData: termsDetails.map(t => ({ Id: t.TermsAndConditionId || t.Id, Name: t.Name || '', IsInternational: t.IsInternational || false })),
          TermsAndConditions: termsDetails.map(t => t.Name).join('\n'),
          // Shipping & Delivery fields
          PortOfLoading: poData.PortOfLoading || '',
          PortOfDischarge: poData.PortOfDischarge || '',
          ModeOfShipment: poData.ModeOfShipment || '',
          PartialShipment: poData.PartialShipment || '',
          Transhipment: poData.Transhipment || '',
          Incoterms: poData.Incoterms || '',
          PaymentTerms: poData.PaymentTerms || '',
          RequestedShipmentDate: poData.RequestedShipmentDate?.split('T')[0] || '',
          DeliveryLocation: poData.DeliveryLocation || '',
          RequiredDeliveryDate: poData.RequiredDeliveryDate?.split('T')[0] || '',
          RequestedBy: poData.RequestedBy || ''
        });

        // Log the API response to debug
        console.log('PO Data from API:', poData);
        console.log('Amount fields:', {
          Amount: poData.Amount,
          TaxableAmount: poData.TaxableAmount,
          VATAmount: poData.VATAmount,
          TotalAmount: poData.TotalAmount
        });
        setOrderDetails(details.map(d => {
          // Get item name from ItemName or find from items list
          const itemName = d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '';
          return {
            Id: d.Id || 0,
            ItemId: d.ItemId,
            ItemName: itemName,
            HSNCode: d.HSNCode || '',
            VATRate: d.VATRate || 0,
            Quantity: d.Quantity || 0,
            Rate: d.Rate || 0,
            Amount: d.Amount || 0,
            DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0,
            TaxableAmount: d.TaxableAmount || 0,
            VATAmount: d.VATAmount || 0,
            TotalAmount: d.TotalAmount || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy || null,
            CreatedDate: d.CreatedDate || d.CreatedOn || null
          };
        }));
      } else {
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
        setOrderDetails([]);
      }

      setLoading(false);
    } else {
      // Create mode - fetch max bill number and freight/packing GST
      setLoading(true);
      setLoadingMessage('Generating PO number...');

      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();

      // Get VAT rates from API data
      const freightVATRate = gstData.freight?.VATRate || gstData.freight?.GSTRate || gstData.freight?.GstRate || 0;
      const packingVATRate = gstData.packing?.VATRate || gstData.packing?.GSTRate || gstData.packing?.GstRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        SupplierId: '',
        InvoiceTypeId: 0,
        PONumber: maxBillData.billNumber,
        PurchaseOrderNo: maxBillData.purchaseOrderNo,
        PurchaseOrderDate: new Date().toISOString().split('T')[0],
        FinYear: maxBillData.finYear,
        CurrencyTypeId: null,
        CurrencyName: '',
        Instruction1: '',
        Instruction2: '',
        Remarks: '',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        VATAmount: 0,
        FreightAmount: 0,
        FreightVATRate: freightVATRate,
        FreightVATAmount: 0,
        PackingAmount: 0,
        PackingVATRate: packingVATRate,
        PackingVATAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        PaidAmount: 0,
        BalanceAmount: 0,
        IsConverted: false,
        IsVerified: false,
        IsActive: true,
        // Shipping & Delivery fields
        PortOfLoading: '',
        PortOfDischarge: '',
        ModeOfShipment: '',
        PartialShipment: '',
        Transhipment: '',
        Incoterms: '',
        PaymentTerms: '',
        RequestedShipmentDate: '',
        DeliveryLocation: '',
        RequiredDeliveryDate: '',
        RequestedBy: ''
      });
      setOrderDetails([]);
      setLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setOrderDetails([]);
    window.location.reload();
  };


  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} purchase order...`);

      // Get selected supplier details for MasterId, MasterName and InvoiceTypeId
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.SupplierId));
      const masterId = selectedSupplier?.MasterId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      // Try multiple field names for InvoiceTypeId
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || selectedSupplier?.invoiceTypeId ||
        selectedSupplier?.InvoiceType || selectedSupplier?.invoiceType ||
        formData.InvoiceTypeId || 0;

      // Debug log to see supplier data
      console.log('Selected Supplier:', selectedSupplier);
      console.log('InvoiceTypeId:', invoiceTypeId);

      // Calculate total quantity from all order details
      const totalQuantity = orderDetails.reduce((sum, detail) => sum + (parseFloat(detail.Quantity) || 0), 0);

      const dataToSend = {
        PurchaseOrder: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || 0,
          MasterId: masterId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          PONumber: formData.PONumber || 0,
          BillNumber: formData.PONumber || 0,
          PurchaseOrderNo: formData.PurchaseOrderNo || '',
          PurchaseOrderDate: formData.PurchaseOrderDate || new Date().toISOString().split('T')[0],
          FinYear: formData.FinYear || '',
          Instruction1: formData.Instruction1 || '',
          Instruction2: formData.Instruction2 || '',
          Remarks: formData.Remarks || '',
          TotalQuantity: totalQuantity,
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          VATAmount: parseFloat(formData.VATAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightVATRate: parseFloat(formData.FreightVATRate) || 0,
          FreightVATAmount: parseFloat(formData.FreightVATAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingVATRate: parseFloat(formData.PackingVATRate) || 0,
          PackingVATAmount: parseFloat(formData.PackingVATAmount) || 0,
          RoundOff: Math.round((parseFloat(formData.RoundOff) || 0) * 100) / 100,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          PaidAmount: parseFloat(formData.PaidAmount) || 0,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          CurrencyName: formData.CurrencyName || '',
          CurrencyTypeId: formData.CurrencyTypeId || null,
          IsConverted: formData.IsConverted || false,
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'edit' ? formData.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null,
          // Shipping & Delivery fields
          PortOfLoading: formData.PortOfLoading || '',
          PortOfDischarge: formData.PortOfDischarge || '',
          ModeOfShipment: formData.ModeOfShipment || '',
          PartialShipment: formData.PartialShipment || '',
          Transhipment: formData.Transhipment || '',
          Incoterms: formData.Incoterms || '',
          PaymentTerms: formData.PaymentTerms || '',
          RequestedShipmentDate: formData.RequestedShipmentDate || null,
          DeliveryLocation: formData.DeliveryLocation || '',
          RequiredDeliveryDate: formData.RequiredDeliveryDate || null,
          RequestedBy: formData.RequestedBy || ''
        },
        PurchaseOrderDetails: orderDetails.map(detail => {
          // Get item name from items dropdown
          const selectedItem = items.find(i => i.Id === parseInt(detail.ItemId));
          const itemName = selectedItem?.Name || '';

          return {
            Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
            CompanyId: companyId,
            BranchId: formData.BranchId || 0,
            PurchaseOrderId: modalAction === 'edit' ? formData.Id : 0,
            ItemId: parseInt(detail.ItemId) || 0,
            ItemName: itemName,
            HSNCode: detail.HSNCode || '',
            VATRate: parseFloat(detail.VATRate) || 0,
            Quantity: parseFloat(detail.Quantity) || 0,
            Rate: parseFloat(detail.Rate) || 0,
            Amount: parseFloat(detail.Amount) || 0,
            DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
            TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
            VATAmount: parseFloat(detail.VATAmount) || 0,
            TotalAmount: parseFloat(detail.TotalAmount) || 0,
            IsActive: detail.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
            CreatedDate: modalAction === 'edit' ? detail.CreatedDate : null,
            ModifiedBy: modalAction === 'edit' ? userId : null
          };
        }),
        PurchaseTermsDetails: (formData.SelectedTermsData && formData.SelectedTermsData.length > 0) 
          ? formData.SelectedTermsData.map(term => ({
              Id: 0,
              CompanyId: companyId,
              TermsAndConditionId: term.Id,
              PurchaseOrderId: modalAction === 'edit' ? (formData.Id || 0) : 0,
              Name: term.Name || '',
              IsActive: true,
              CreatedBy: userId,
              ModifiedBy: modalAction === 'edit' ? userId : null,
              ModifiedDate: modalAction === 'edit' ? new Date().toISOString() : null
            }))
          : []
      };

      if (modalAction === 'create') {
        await postData('PurchaseOrderApi', '', dataToSend, token);
        setSuccess('Purchase order created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Purchase Order ID is missing');
        await updateData('PurchaseOrderApi', formData.Id, dataToSend, token);
        setSuccess('Purchase order updated successfully!');
      }

      handleCloseModal();
      await fetchPurchaseOrders();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} purchase order. Please try again.`);
      setLoading(false);
    }
  };

  // Verify handler
  const handleVerify = async (id) => {
    if (!id) return;

    setLoading(true);
    setLoadingMessage('Verifying purchase order...');
    try {
      await ConvertPurchaseOrderVerify('PurchaseOrderApi', id, token);
      setSuccess('Purchase order verified successfully!');
      handleCloseModal();
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error verifying:', error);
      setError('Failed to verify purchase order.');
    }
    setLoading(false);
  };

  // Convert handler
  const handleConvert = async (id) => {
    if (!id) return;

    setLoading(true);
    setLoadingMessage('Converting to purchase...');
    try {
      await ConvertPurchaseOrderToPurchase('PurchaseOrderApi', id, token);
      setSuccess('Purchase order converted to purchase successfully!');
      handleCloseModal();
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error converting to purchase:', error);
      setError('Failed to convert purchase order.');
    }
    setLoading(false);
  };

  // Optimized supplier click handler
  const handleSupplierClick = useCallback(async (supplierId, supplierName) => {
    setLoading(true);
    setLoadingMessage('Fetching supplier purchase orders...');
    try {
      const response = await fetchData('PurchaseOrderApi', `/PurchaseOrderHistoryByMaster/${supplierId}/${branchId}`, token);
      const responseData = response.response_data || response || [];

      // Handle the response structure - it's an array with PurchaseOrder and PurchaseOrderDetails
      let purchaseOrdersList = [];
      let purchaseOrderDetailsList = [];

      if (Array.isArray(responseData) && responseData.length > 0) {
        // Extract PurchaseOrder and PurchaseOrderDetails from the first item
        const firstItem = responseData[0];
        purchaseOrdersList = firstItem.PurchaseOrder || firstItem.purchaseOrder || [];
        purchaseOrderDetailsList = firstItem.PurchaseOrderDetails || firstItem.purchaseOrderDetails || [];
      } else if (responseData.PurchaseOrder) {
        purchaseOrdersList = responseData.PurchaseOrder || [];
        purchaseOrderDetailsList = responseData.PurchaseOrderDetails || [];
      }

      // Map details to each purchase order
      const purchaseOrdersWithDetails = purchaseOrdersList.map(po => {
        const details = purchaseOrderDetailsList.filter(detail => detail.PurchaseOrderId === po.Id);
        return { ...po, PurchaseOrderDetails: details };
      });

      setSelectedSupplier({ id: supplierId, name: supplierName });
      setSupplierPurchaseOrders(purchaseOrdersWithDetails);
      setExpandedPOId(null);
      setSupplierModalOpen(true);
    } catch (error) {
      console.error('Error fetching supplier purchase orders:', error);
      // Fallback: filter from existing data
      const filteredPOs = purchaseOrders.filter(po => po.MasterId === supplierId || po.SupplierId === supplierId);
      setSelectedSupplier({ id: supplierId, name: supplierName });
      setSupplierPurchaseOrders(filteredPOs);
      setExpandedPOId(null);
      setSupplierModalOpen(true);
    }
    setLoading(false);
  }, [branchId, token, purchaseOrders]);

  const handleCloseSupplierModal = () => {
    setSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierPurchaseOrders([]);
    setExpandedPOId(null);
  };

  const togglePOExpand = async (poId) => {
    if (expandedPOId === poId) {
      setExpandedPOId(null);
    } else {
      setExpandedPOId(poId);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, poNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(poNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('PurchaseOrderApi', deleteItemId, token);
      setSuccess('Purchase order deleted successfully!');
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete purchase order.');
    }
    setLoading(false);
  };

  // Filter and pagination - Memoized for performance
  const getFilteredData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    let filtered = data;

    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.PurchaseOrderNo?.toLowerCase().includes(searchLower) ||
        item.MasterName?.toLowerCase().includes(searchLower) ||
        item.SupplierName?.toLowerCase().includes(searchLower)
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    // Date range filter
    if (fromDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.PurchaseOrderDate?.split('T')[0];
        return itemDate >= fromDate;
      });
    }

    if (toDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.PurchaseOrderDate?.split('T')[0];
        return itemDate <= toDate;
      });
    }

    return filtered;
  }, [debouncedSearchTerm, filterVerified, fromDate, toDate]);

  // Memoized pagination data
  const paginationData = useMemo(() => {
    const filtered = getFilteredData(purchaseOrders) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  }, [purchaseOrders, getFilteredData, currentPage, rowsPerPage]);

  // Memoized supplier lookup
  const supplierLookup = useMemo(() => {
    const lookup = {};
    suppliers.forEach(supplier => {
      const id = supplier.MasterId || supplier.Id;
      if (id) {
        lookup[id] = supplier;
      }
    });
    return lookup;
  }, [suppliers]);

  // Memoized items lookup
  const itemsLookup = useMemo(() => {
    const lookup = {};
    items.forEach(item => {
      if (item.Id) {
        lookup[item.Id] = item;
      }
    });
    return lookup;
  }, [items]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);
  const handleRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }, []);
  const handleRefresh = useCallback(() => fetchAllData(), []);
  const handleCloseSnackbar = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  // Print individual Purchase Order handler - Professional Format
  const handlePrintPO = async (poId) => {
    // Open print window immediately on user click to avoid popup blocker
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Popup blocked. Please allow popups for this site.');
      return;
    }
    // Show a loading message in the print window while data is fetched
    printWindow.document.write('<html><head><title>Loading...</title></head><body style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:Arial;"><h2 style="color:#1e3a5f;">Preparing Purchase Order...</h2></body></html>');

    setLoading(true);
    setLoadingMessage('Preparing print...');

    try {
      const freshData = await fetchSinglePurchaseOrder(poId);

      if (!freshData) {
        setError('Failed to fetch purchase order details for printing.');
        setLoading(false);
        printWindow.close();
        return;
      }

      const poData = freshData.PurchaseOrder || freshData;
      const details = freshData.PurchaseOrderDetails || freshData.Details || [];
      const termsDetails = freshData.PurchaseTermsDetails || [];

      // Get supplier details
      let supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId);

      // Fetch full supplier details to get TradeLicenseNo and other fields
      if (poData.MasterId) {
        try {
          const supplierResponse = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${poData.MasterId}`, token);
          const supplierData = supplierResponse.response_data?.Ledger || supplierResponse.response_data || supplierResponse || {};
          supplier = { ...supplier, ...supplierData };
        } catch (err) {
          console.error('Error fetching supplier details for print:', err);
        }
      }

      const supplierName = poData.MasterName || supplier?.Name || '-';
      const supplierAddress = supplier?.Address || supplier?.AddressLine1 || poData.SupplierAddress || '';
      const supplierAddress2 = supplier?.AddressLine2 || '';
      const supplierLocation = supplier?.Location || '';
      const supplierGST = supplier?.VATNumber || supplier?.GSTNumber || poData.VATNumber || '';
      const supplierContact = supplier?.ContactNumber1 || supplier?.ContactNumber || supplier?.Mobile || '';
      const supplierEmail = supplier?.Email || '';
      const supplierIEC = poData?.IECCode || '';

      // Company name from auth context  
      const companyNameDisplay = companyName || 'Company Name';

      // Determine if International PO (non-AED currency)
      const currencyName = poData.CurrencyName || supplier?.CurrencyName || '';
      const isInternationalPO = currencyName && !['aed', 'dirham', 'uae dirham'].includes(currencyName.toLowerCase().trim());

      // Get Country of Origin from supplier's CountryId
      let countryOfOrigin = supplier?.CountryName || supplier?.Country || '';
      if (!countryOfOrigin && supplier?.CountryId) {
        try {
          const countryResponse = await fetchData('CountryApi', `/${companyId}`, token);
          const countriesList = countryResponse.response_data || countryResponse || [];
          const matchedCountry = countriesList.find(c => c.Id === supplier.CountryId);
          countryOfOrigin = matchedCountry?.CountryName || matchedCountry?.Name || '';
        } catch (err) {
          console.error('Error fetching country for PO print:', err);
        }
      }

      // printWindow already opened at the top of this function

      if (isInternationalPO) {
        // ===== INTERNATIONAL PURCHASE ORDER FORMAT =====
        const currencyCode = currencyName.toUpperCase().trim();
        const currencySymbolMap = {
          'INR': '₹', 'RUPEE': '₹', 'INDIAN RUPEE': '₹',
          'USD': '$', 'DOLLAR': '$', 'US DOLLAR': '$',
          'EUR': '€', 'EURO': '€',
          'GBP': '£', 'POUND': '£',
          'SAR': '﷼', 'RIYAL': '﷼',
          'QAR': 'QR', 'KWD': 'KD', 'OMR': 'OMR', 'BHD': 'BD'
        };
        const cSymbol = currencySymbolMap[currencyCode] || currencyCode;

        const formatDate = (dateStr) => {
          if (!dateStr) return '';
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return dateStr;
          return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        };

        // Fetch branch details for company header
        let branchDetails = { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '', iecCode: '' };
        try {
          const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
          if (branchResponse.responseCode === '1111' || branchResponse.response_data) {
            const branchData = branchResponse.response_data || branchResponse || {};
            branchDetails = {
              branchName: branchData.Name || '',
              location: branchData.Location || '',
              addressLine1: branchData.AddressLine1 || '',
              addressLine2: branchData.AddressLine2 || '',
              pincode: branchData.PinCode || '',
              companyName: branchData.CompanyName || branchData.Name || '',
              gstNumber: branchData.GSTNumber || branchData.VATNumber || branchData.CompanyGSTNumber || '',
              iecCode: branchData.IECCode || branchData.IecCode || branchData.ImporterCode || ''
            };
          }
        } catch (err) {
          console.error('Error fetching branch details for print:', err);
        }

        // Convert logo to base64 for print window
        let logoBase64 = '';
        try {
          const resp = await fetch(BpsGlobalLogo);
          const blob = await resp.blob();
          logoBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch (err) {
          console.error('Error converting logo to base64:', err);
        }

        // Build item rows for international format
        let intItemRows = '';
        let goodsTotal = 0;
        details.forEach((d, idx) => {
          const itemData = items.find(i => i.Id === d.ItemId);
          const itemName = d.ItemName || itemData?.Name || '-';
          const qty = parseFloat(d.Quantity) || 0;
          const rate = parseFloat(d.Rate) || 0;
          const totalAmt = parseFloat(d.TaxableAmount) || (qty * rate);
          goodsTotal += totalAmt;

          intItemRows += '<tr>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + (idx + 1) + '</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:left;font-size:9px;">' + itemName + '</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + (d.HSNCode || '-') + '</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + (countryOfOrigin || '-') + '</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + qty + '</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:right;font-size:9px;">' + rate.toFixed(2) + '</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:right;font-size:9px;">' + totalAmt.toFixed(2) + '</td>' +
            '</tr>';
        });

        // Empty rows to fill minimum 5
        const minRows = 5;
        for (let i = details.length; i < minRows; i++) {
          intItemRows += '<tr>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + (i + 1) + '</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td>' +
            '<td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td>' +
            '</tr>';
        }

        const freightInsurance = (parseFloat(poData.FreightAmount) || 0) + (parseFloat(poData.PackingAmount) || 0);
        const grandTotal = goodsTotal + freightInsurance;

        // International T&C (filter IsInternational)
        const internationalTerms = termsDetails.filter(t => t.IsInternational);
        const localTerms = termsDetails.filter(t => !t.IsInternational);

        const intPrintContent = '<!DOCTYPE html><html><head><title>International PO-' + poData.PurchaseOrderNo + '</title>' +
          '<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10px;color:#1e293b}' +
          'table{width:100%;border-collapse:collapse}' +
          '@media print{.no-print{display:none!important}@page{margin:8mm;size:A4}}</style>' +
          '</head><body style="padding:10px;width:94%;margin:10px auto;">' +

          // Action bar
          '<div class="no-print" style="position:fixed;top:0;left:0;right:0;background:#1e3a5f;padding:8px 15px;display:flex;justify-content:space-between;align-items:center;z-index:999;">' +
          '<span style="color:white;font-size:13px;font-weight:600;">International Purchase Order: ' + poData.PurchaseOrderNo + '</span>' +
          '<div><button onclick="window.close()" style="padding:6px 18px;margin-right:10px;border:1px solid rgba(255,255,255,0.4);border-radius:4px;background:transparent;color:white;cursor:pointer;font-size:12px;">Cancel</button>' +
          '<button onclick="window.print()" style="padding:6px 18px;border:none;border-radius:4px;background:white;color:#1e3a5f;cursor:pointer;font-size:12px;font-weight:600;">Print</button></div></div>' +

          '<div style="margin-top:45px;">' +

          // === COMPANY HEADER: Logo left + Branch details right ===
          '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #1e3a5f;">' +
          '<div style="display:flex;align-items:center;gap:10px;">' +
          (logoBase64 ? '<img src="' + logoBase64 + '" style="height:50px;width:auto;" />' : '') +
          '<div style="font-size:16px;font-weight:bold;color:#1e3a5f;">' + companyNameDisplay + '</div>' +
          '</div>' +
          '<div style="text-align:right;font-size:9px;line-height:1.6;color:#374151;">' +
          (branchDetails.branchName ? '<div style="font-weight:bold;font-size:10px;color:#1e3a5f;">' + branchDetails.branchName + '</div>' : '') +
          (branchDetails.addressLine1 ? '<div>' + branchDetails.addressLine1 + '</div>' : '') +
          (branchDetails.addressLine2 ? '<div>' + branchDetails.addressLine2 + '</div>' : '') +
          (branchDetails.location || branchDetails.pincode ? '<div>' + (branchDetails.location || '') + (branchDetails.pincode ? ' - ' + branchDetails.pincode : '') + '</div>' : '') +
          (branchDetails.gstNumber ? '<div><strong>TRN:</strong> ' + branchDetails.gstNumber + '</div>' : '') +
          '</div>' +
          '</div>' +

          // === HEADER: International Purchase Order ===
          '<div style="text-align:center;margin-bottom:10px;">' +
          '<h1 style="font-size:18px;font-weight:bold;color:#1e3a5f;border-bottom:3px solid #1e3a5f;display:inline-block;padding-bottom:4px;">International Purchase Order</h1>' +
          '</div>' +

          // === PO DETAILS + SUPPLIER SECTION ===
          '<table style="margin-bottom:10px;"><tr>' +
          // Left: Purchase Order Details
          '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;">' +
          '<div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">PURCHASE ORDER DETAILS</div>' +
          '<table style="font-size:9px;line-height:1.8;border:none;border-collapse:collapse;width:100%;">' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;width:140px;border:none;">PO No.:</td><td style="border:none;">' + poData.PurchaseOrderNo + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">PO Date:</td><td style="border:none;">' + formatDate(poData.PurchaseOrderDate) + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Incoterms® 2020:</td><td style="border:none;">' + (poData.Incoterms || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Currency:</td><td style="border:none;">' + currencyCode + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Payment Terms:</td><td style="border:none;">' + (poData.PaymentTerms || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Shipment Date:</td><td style="border:none;">' + (poData.RequestedShipmentDate ? formatDate(poData.RequestedShipmentDate) : '-') + '</td></tr>' +
          '</table></td>' +
          // Right: Supplier / Shipper
          '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;">' +
          '<div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">SUPPLIER / SHIPPER (SELLER)</div>' +
          '<table style="font-size:9px;line-height:1.8;border:none;border-collapse:collapse;width:100%;">' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;width:110px;border:none;">Company:</td><td style="border:none;">' + supplierName + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Address:</td><td style="border:none;">' + (supplierAddress + (supplierAddress2 ? ', ' + supplierAddress2 : '') + (supplierLocation ? ', ' + supplierLocation : '')) + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">GSTIN:</td><td style="border:none;">' + (supplierGST || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">IEC Code:</td><td style="border:none;">' + (supplierIEC || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Tel / Email:</td><td style="border:none;">' + supplierContact + ' / ' + supplierEmail + '</td></tr>' +
          '</table></td>' +
          '</tr></table>' +

          // === CONSIGNEE / BUYER + SHIPMENT DETAILS ===
          '<table style="margin-bottom:10px;"><tr>' +
          // Left: Consignee / Buyer
          '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;">' +
          '<div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">CONSIGNEE / BUYER</div>' +
          '<table style="font-size:9px;line-height:1.8;border:none;border-collapse:collapse;width:100%;">' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;width:120px;border:none;">Company:</td><td style="border:none;">' + companyNameDisplay + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Address:</td><td style="border:none;">' + (branchDetails.addressLine1 || '') + (branchDetails.location ? ', ' + branchDetails.location : '') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">TRN:</td><td style="border:none;">' + (branchDetails.gstNumber || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Importer Code:</td><td style="border:none;">' + (branchDetails.iecCode || '-') + '</td></tr>' +
          '</table></td>' +
          // Right: Shipment Details
          '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;">' +
          '<div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">SHIPMENT DETAILS</div>' +
          '<table style="font-size:9px;line-height:1.8;border:none;border-collapse:collapse;width:100%;">' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;width:130px;border:none;">Port of Loading:</td><td style="border:none;">' + (poData.PortOfLoading || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Port of Discharge:</td><td style="border:none;">' + (poData.PortOfDischarge || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Mode of Shipment:</td><td style="border:none;">' + (poData.ModeOfShipment || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Partial Shipment:</td><td style="border:none;">' + (poData.PartialShipment || '-') + '</td></tr>' +
          '<tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Transhipment:</td><td style="border:none;">' + (poData.Transhipment || '-') + '</td></tr>' +
          '</table></td>' +
          '</tr></table>' +

          // === ITEMS TABLE ===
          '<table style="margin-bottom:10px;">' +
          '<thead><tr style="background:#1e3a5f;">' +
          '<th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:9px;font-weight:bold;color:#fff;width:5%;">S.No</th>' +
          '<th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:9px;font-weight:bold;color:#fff;width:30%;">Description of Goods</th>' +
          '<th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:9px;font-weight:bold;color:#fff;width:12%;">HS / Product Code</th>' +
          '<th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:9px;font-weight:bold;color:#fff;width:12%;">Country of Origin</th>' +
          '<th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:9px;font-weight:bold;color:#fff;width:10%;">Qty (Units)</th>' +
          '<th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:9px;font-weight:bold;color:#fff;width:13%;">Unit Price (' + currencyCode + ')</th>' +
          '<th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:9px;font-weight:bold;color:#fff;width:15%;">Total (' + currencyCode + ')</th>' +
          '</tr></thead><tbody>' + intItemRows + '</tbody></table>' +

          // === TOTALS ===
          '<table style="margin-bottom:10px;"><tr>' +
          '<td style="width:60%;"></td>' +
          '<td style="width:40%;">' +
          '<table style="border:1.5px solid #1e3a5f;">' +
          '<tr><td style="border:1px solid #1e3a5f;padding:6px 10px;font-size:9px;font-weight:bold;">Goods Total (' + (poData.Incoterms || 'FOB') + ' Value, ' + currencyCode + '):</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 10px;text-align:right;font-size:10px;font-weight:bold;">' + goodsTotal.toFixed(2) + '</td></tr>' +
          '<tr><td style="border:1px solid #1e3a5f;padding:6px 10px;font-size:9px;font-weight:bold;">Freight & Insurance (if CIF/CIP):</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 10px;text-align:right;font-size:10px;">' + (freightInsurance > 0 ? freightInsurance.toFixed(2) : '[XXXXXX]') + '</td></tr>' +
          '<tr style="background:#f0f4ff;"><td style="border:1px solid #1e3a5f;padding:8px 10px;font-size:10px;font-weight:bold;">Grand Total (' + currencyCode + '):</td>' +
          '<td style="border:1px solid #1e3a5f;padding:8px 10px;text-align:right;font-size:11px;font-weight:bold;">' + grandTotal.toFixed(2) + '</td></tr>' +
          '</table></td></tr></table>' +

          // === INTERNATIONAL TRADE TERMS & CONDITIONS ===
          (termsDetails.length > 0 ?
          '<div style="margin-bottom:10px;">' +
          '<div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:2px solid #1e3a5f;padding-bottom:3px;margin-bottom:8px;">International Trade Terms &amp; Conditions</div>' +
          '<div style="font-size:8px;line-height:1.7;">' +
          termsDetails.map((t, i) => '<div style="margin-bottom:3px;">' + (i + 1) + '. ' + (t.Name || '') + '</div>').join('') +
          '</div></div>' : '') +

          // === DOCUMENTS REQUIRED + AUTHORISED SIGNATORY ===
          '<table style="margin-bottom:10px;"><tr>' +
          '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;">' +
          '<div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">DOCUMENTS REQUIRED</div>' +
          '<div style="font-size:9px;line-height:1.8;">' +
          '<div>- Commercial Invoice &amp; Packing List</div>' +
          '<div>- Bill of Lading / Airway Bill</div>' +
          '<div>- Certificate of Origin</div>' +
          '<div>- Insurance Certificate (if CIF/CIP)</div>' +
          '</div></td>' +
          '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;">' +
          '<div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">AUTHORISED SIGNATORY</div>' +
          '<div style="font-size:9px;line-height:1.8;">' +
          '<div><strong>For ' + companyNameDisplay + ' (Buyer)</strong></div>' +
          '<div style="margin-top:30px;border-top:1px solid #999;padding-top:4px;font-style:italic;">Signature &amp; Company Stamp</div>' +
          '</div></td>' +
          '</tr></table>' +

          // === NOTE ===
          '<div style="margin-top:10px;padding:8px;border:1px solid #93c5fd;background:#eff6ff;border-radius:3px;">' +
          '<div style="font-size:8px;color:#1e3a5f;"><strong>Note:</strong> This is an export/import transaction. UAE VAT is not charged by the Supplier at source. Import VAT (if applicable) shall be self-accounted for by the Buyer under the UAE reverse-charge mechanism at the point of customs clearance.</div>' +
          '</div>' +

          '<div style="text-align:center;margin-top:12px;font-size:9px;font-weight:bold;color:#1e3a5f;">www.bpsglobaltrading.com</div>' +

          '</div></body></html>';

        if (printWindow) {
          printWindow.document.open();
          printWindow.document.write(intPrintContent);
          printWindow.document.close();
        }

        setLoading(false);
        return;
      }

      // ===== LOCAL / AED PURCHASE ORDER FORMAT =====

      // Fetch branch details for local PO
      let localBranchDetails = { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '', tradeLicenseNo: '' };
      try {
        const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
        if (branchResponse.responseCode === '1111' || branchResponse.response_data) {
          const branchData = branchResponse.response_data || branchResponse || {};
          localBranchDetails = {
            branchName: branchData.Name || '',
            location: branchData.Location || '',
            addressLine1: branchData.AddressLine1 || '',
            addressLine2: branchData.AddressLine2 || '',
            pincode: branchData.PinCode || '',
            companyName: branchData.CompanyName || branchData.Name || '',
            gstNumber: branchData.GSTNumber || branchData.VATNumber || branchData.CompanyGSTNumber || '',
            tradeLicenseNo: branchData.TradeLicenseNo || branchData.TradeLicense || ''
          };
        }
      } catch (err) {
        console.error('Error fetching branch details for local print:', err);
      }

      // Convert logo to base64
      let localLogoBase64 = '';
      try {
        const resp = await fetch(BpsGlobalLogo);
        const blob = await resp.blob();
        localLogoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (err) {
        console.error('Error converting logo to base64:', err);
      }

      const formatLocalDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      };

      // Build item rows for local format
      let localItemRows = '';
      let localSubtotal = 0;
      let localTotalVAT = 0;
      details.forEach((d, idx) => {
        const itemData = items.find(i => i.Id === d.ItemId);
        const itemName = d.ItemName || itemData?.Name || '-';
        const qty = parseFloat(d.Quantity) || 0;
        const rate = parseFloat(d.Rate) || 0;
        const taxableAmount = parseFloat(d.TaxableAmount) || (qty * rate);
        const vatAmount = parseFloat(d.VATAmount) || (taxableAmount * 0.05);
        const totalAmount = parseFloat(d.TotalAmount) || (taxableAmount + vatAmount);
        localSubtotal += taxableAmount;
        localTotalVAT += vatAmount;

        localItemRows += '<tr>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + (idx + 1) + '</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:left;font-size:9px;">' + itemName + '</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + (d.HSNCode || '-') + '</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + qty + '</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:right;font-size:9px;">' + rate.toFixed(2) + '</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:right;font-size:9px;">' + taxableAmount.toFixed(2) + '</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:right;font-size:9px;">' + vatAmount.toFixed(2) + '</td>' +
          '<td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:right;font-size:9px;">' + totalAmount.toFixed(2) + '</td>' +
          '</tr>';
      });

      for (let i = details.length; i < 5; i++) {
        localItemRows += '<tr><td style="border:1px solid #1e3a5f;padding:6px 8px;text-align:center;font-size:9px;">' + (i + 1) + '</td><td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td><td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td><td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td><td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td><td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td><td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td><td style="border:1px solid #1e3a5f;padding:6px 8px;">&nbsp;</td></tr>';
      }

      const localGrandTotal = parseFloat(poData.TotalAmount) || (localSubtotal + localTotalVAT);

      // Amount in words
      const numToWords = (num) => { const ones=['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen']; const tens=['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety']; const cg=(n)=>{if(n===0)return '';if(n<20)return ones[n];if(n<100)return tens[Math.floor(n/10)]+(n%10?' '+ones[n%10]:'');return ones[Math.floor(n/100)]+' Hundred'+(n%100?' and '+cg(n%100):'');}; const a=Math.abs(num);const w=Math.floor(a);const d=Math.round((a-w)*100);let r='';if(w>=1000000){r+=cg(Math.floor(w/1000000))+' Million ';const rm=w%1000000;if(rm>=1000)r+=cg(Math.floor(rm/1000))+' Thousand ';const r2=rm%1000;if(r2>0)r+=cg(r2);}else if(w>=1000){r+=cg(Math.floor(w/1000))+' Thousand ';const rm=w%1000;if(rm>0)r+=cg(rm);}else{r=cg(w);}r=r.trim();if(d>0)r+=' and '+d+' fils';return r; };
      const amountInWords = 'UAE Dirhams ' + numToWords(localGrandTotal) + ' Only.';
      const localTermsList = termsDetails.filter(t => !t.IsInternational);

      const localPrintContent = '<!DOCTYPE html><html><head><title>LPO-' + poData.PurchaseOrderNo + '</title>' +
        '<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10px;color:#1e293b}table{width:100%;border-collapse:collapse}@media print{.no-print{display:none!important}@page{margin:8mm;size:A4}}</style></head><body style="padding:10px;width:94%;margin:10px auto;">' +
        '<div class="no-print" style="position:fixed;top:0;left:0;right:0;background:#1e3a5f;padding:8px 15px;display:flex;justify-content:space-between;align-items:center;z-index:999;"><span style="color:white;font-size:13px;font-weight:600;">Local Purchase Order: ' + poData.PurchaseOrderNo + '</span><div><button onclick="window.close()" style="padding:6px 18px;margin-right:10px;border:1px solid rgba(255,255,255,0.4);border-radius:4px;background:transparent;color:white;cursor:pointer;font-size:12px;">Cancel</button><button onclick="window.print()" style="padding:6px 18px;border:none;border-radius:4px;background:white;color:#1e3a5f;cursor:pointer;font-size:12px;font-weight:600;">Print</button></div></div>' +
        '<div style="margin-top:45px;">' +
        '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;padding-bottom:8px;border-bottom:2px solid #1e3a5f;">' +
        '<div style="display:flex;align-items:center;gap:10px;">' + (localLogoBase64 ? '<img src="' + localLogoBase64 + '" style="height:50px;width:auto;" />' : '') + '<div style="font-size:16px;font-weight:bold;color:#1e3a5f;">' + companyNameDisplay + '</div></div>' +
        '<div style="text-align:right;font-size:9px;line-height:1.6;color:#374151;">' + (localBranchDetails.branchName ? '<div style="font-weight:bold;font-size:10px;color:#1e3a5f;">' + localBranchDetails.branchName + '</div>' : '') + (localBranchDetails.addressLine1 ? '<div>' + localBranchDetails.addressLine1 + '</div>' : '') + (localBranchDetails.addressLine2 ? '<div>' + localBranchDetails.addressLine2 + '</div>' : '') + (localBranchDetails.location || localBranchDetails.pincode ? '<div>' + (localBranchDetails.location || '') + (localBranchDetails.pincode ? ' - ' + localBranchDetails.pincode : '') + '</div>' : '') + (localBranchDetails.tradeLicenseNo ? '<div><strong>Trade License No:</strong> ' + localBranchDetails.tradeLicenseNo + '</div>' : '') + (localBranchDetails.gstNumber ? '<div><strong>TRN:</strong> ' + localBranchDetails.gstNumber + '</div>' : '') + '</div></div>' +
        '<div style="text-align:center;margin-bottom:10px;"><h1 style="font-size:18px;font-weight:bold;color:#1e3a5f;border-bottom:3px solid #1e3a5f;display:inline-block;padding-bottom:4px;">Local Purchase Order (LPO)</h1></div>' +
        '<table style="margin-bottom:10px;"><tr>' +
        '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;"><div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">PURCHASE ORDER DETAILS</div><table style="font-size:9px;line-height:1.8;border:none;border-collapse:collapse;width:100%;"><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;width:130px;border:none;">PO No.:</td><td style="border:none;">' + poData.PurchaseOrderNo + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">PO Date:</td><td style="border:none;">' + formatLocalDate(poData.PurchaseOrderDate) + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Required Delivery Date:</td><td style="border:none;">' + (poData.RequiredDeliveryDate ? formatLocalDate(poData.RequiredDeliveryDate) : '-') + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Payment Terms:</td><td style="border:none;">' + (poData.PaymentTerms || '-') + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Delivery Location:</td><td style="border:none;">' + (poData.DeliveryLocation || '-') + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Requested By:</td><td style="border:none;">' + (poData.RequestedBy || '-') + '</td></tr></table></td>' +
        '<td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;"><div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">SUPPLIER DETAILS</div><table style="font-size:9px;line-height:1.8;border:none;border-collapse:collapse;width:100%;"><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;width:120px;border:none;">Supplier Name:</td><td style="border:none;">' + supplierName + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Address:</td><td style="border:none;">' + (supplierAddress + (supplierAddress2 ? ', ' + supplierAddress2 : '') + (supplierLocation ? ', ' + supplierLocation : '')) + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Supplier TRN:</td><td style="border:none;">' + (supplierGST || '-') + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Trade License No.:</td><td style="border:none;">' + (supplier?.TradeLicenseNo || supplier?.LicenseNo || '-') + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Contact Person:</td><td style="border:none;">' + (supplier?.ContactPerson || '-') + '</td></tr><tr><td style="font-weight:bold;white-space:nowrap;padding:2px 8px 2px 0;border:none;">Tel / Email:</td><td style="border:none;">' + supplierContact + ' / ' + supplierEmail + '</td></tr></table></td></tr></table>' +
        '<table style="margin-bottom:10px;"><thead><tr style="background:#1e3a5f;"><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:5%;">S.No</th><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:30%;">Description of Goods / Services</th><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:10%;">HS / Product Code</th><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:8%;">Qty</th><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:12%;">Unit Price (AED)</th><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:13%;">Taxable Amount (AED)</th><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:10%;">VAT @5% (AED)</th><th style="border:1px solid #1e3a5f;padding:6px;text-align:center;font-size:8px;font-weight:bold;color:#fff;width:12%;">Total (AED)</th></tr></thead><tbody>' + localItemRows + '</tbody></table>' +
        '<table style="margin-bottom:10px;"><tr><td style="width:60%;"></td><td style="width:40%;"><table style="border:1.5px solid #1e3a5f;"><tr><td style="border:1px solid #1e3a5f;padding:6px 10px;font-size:9px;font-weight:bold;">Subtotal (Taxable Amount):</td><td style="border:1px solid #1e3a5f;padding:6px 10px;text-align:right;font-size:10px;font-weight:bold;">' + localSubtotal.toFixed(2) + '</td></tr><tr><td style="border:1px solid #1e3a5f;padding:6px 10px;font-size:9px;font-weight:bold;">Total VAT @ 5%:</td><td style="border:1px solid #1e3a5f;padding:6px 10px;text-align:right;font-size:10px;">' + localTotalVAT.toFixed(2) + '</td></tr><tr style="background:#f0f4ff;"><td style="border:1px solid #1e3a5f;padding:8px 10px;font-size:10px;font-weight:bold;">Grand Total (AED):</td><td style="border:1px solid #1e3a5f;padding:8px 10px;text-align:right;font-size:11px;font-weight:bold;">' + localGrandTotal.toFixed(2) + '</td></tr></table></td></tr></table>' +
        '<div style="margin-bottom:10px;font-size:9px;"><strong style="color:#1e3a5f;">Amount in Words:</strong> <em>' + amountInWords + '</em></div>' +
        '<table style="margin-bottom:10px;"><tr><td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;"><div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">DELIVERY & INSPECTION</div><div style="font-size:9px;line-height:1.8;"><div>Goods/services must match this order in specification,</div><div>quantity and quality.</div><div>Delivery note / service completion report required on</div><div>receipt.</div></div></td><td style="border:1.5px solid #1e3a5f;width:50%;vertical-align:top;padding:10px;"><div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:1px solid #1e3a5f;padding-bottom:3px;margin-bottom:6px;">AUTHORISED SIGNATORY</div><div style="font-size:9px;line-height:1.8;"><div><strong>For ' + companyNameDisplay + '</strong></div><div style="margin-top:30px;border-top:1px solid #999;padding-top:4px;font-style:italic;">Signature & Company Stamp</div></div></td></tr></table>' +
        (localTermsList.length > 0 ? '<div style="margin-bottom:10px;"><div style="font-size:10px;font-weight:bold;color:#1e3a5f;border-bottom:2px solid #1e3a5f;padding-bottom:3px;margin-bottom:8px;">Purchase Terms & Conditions</div><div style="font-size:8px;line-height:1.7;">' + localTermsList.map((t, i) => '<div style="margin-bottom:3px;">' + (i + 1) + '. ' + (t.Name || '') + '</div>').join('') + '</div></div>' : '') +
        '<div style="margin-top:10px;padding:8px;border:1px solid #93c5fd;background:#eff6ff;border-radius:3px;"><div style="font-size:7px;color:#1e3a5f;">This PO is issued by ' + companyNameDisplay + ' in accordance with UAE commercial practice and Federal Decree-Law No.(8) of 2017 on Value Added Tax. The Supplier shall issue a valid FTA-compliant Tax Invoice quoting its TRN for all amounts billed against this order.</div></div>' +
        '<div style="text-align:center;margin-top:12px;font-size:9px;font-weight:bold;color:#1e3a5f;">www.bpsglobaltrading.com</div>' +
        '</div></body></html>';

      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(localPrintContent);
        printWindow.document.close();
      }

    } catch (error) {
      console.error('Error printing PO:', error);
      setError('Failed to print purchase order.');
      if (printWindow) printWindow.close();
    }

    setLoading(false);
  };

  // Download PDF handler for International PO
  const handleDownloadPDF = async (poId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');

    try {
      const freshData = await fetchSinglePurchaseOrder(poId);
      if (!freshData) {
        setError('Failed to fetch purchase order details for PDF.');
        setLoading(false);
        return;
      }

      const poData = freshData.PurchaseOrder || freshData;
      const details = freshData.PurchaseOrderDetails || freshData.Details || [];
      const termsDetails = freshData.PurchaseTermsDetails || [];

      let supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId);

      // Fetch full supplier details to get TradeLicenseNo and other fields
      if (poData.MasterId) {
        try {
          const supplierResponse = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${poData.MasterId}`, token);
          const supplierData = supplierResponse.response_data?.Ledger || supplierResponse.response_data || supplierResponse || {};
          supplier = { ...supplier, ...supplierData };
        } catch (err) {
          console.error('Error fetching supplier details:', err);
        }
      }

      // Determine if International or Local PO
      const currencyName = poData.CurrencyName || supplier?.CurrencyName || '';
      const isInternationalPO = currencyName && !['aed', 'dirham', 'uae dirham'].includes(currencyName.toLowerCase().trim());

      // Fetch branch details
      let branchDetails = { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '', iecCode: '', tradeLicenseNo: '' };
      try {
        const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
        if (branchResponse.responseCode === '1111' || branchResponse.response_data) {
          const branchData = branchResponse.response_data || branchResponse || {};
          branchDetails = {
            branchName: branchData.Name || '',
            location: branchData.Location || '',
            addressLine1: branchData.AddressLine1 || '',
            addressLine2: branchData.AddressLine2 || '',
            pincode: branchData.PinCode || '',
            companyName: branchData.CompanyName || branchData.Name || '',
            gstNumber: branchData.GSTNumber || branchData.VATNumber || branchData.CompanyGSTNumber || '',
            iecCode: branchData.IECCode || branchData.IecCode || branchData.ImporterCode || '',
            tradeLicenseNo: branchData.TradeLicenseNo || branchData.TradeLicense || ''
          };
        }
      } catch (err) {
        console.error('Error fetching branch details:', err);
      }

      if (isInternationalPO) {
        // Get Country of Origin for international
        let countryOfOrigin = supplier?.CountryName || supplier?.Country || '';
        if (!countryOfOrigin && supplier?.CountryId) {
          try {
            const countryResponse = await fetchData('CountryApi', `/${companyId}`, token);
            const countriesList = countryResponse.response_data || countryResponse || [];
            const matchedCountry = countriesList.find(c => c.Id === supplier.CountryId);
            countryOfOrigin = matchedCountry?.CountryName || matchedCountry?.Name || '';
          } catch (err) {
            console.error('Error fetching country:', err);
          }
        }
        await downloadInternationalPOPdf(poData, details, supplier, branchDetails, countryOfOrigin, termsDetails);
      } else {
        await downloadLocalPOPdf(poData, details, supplier, branchDetails, termsDetails);
      }

      setSuccess('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF.');
    }

    setLoading(false);
  };

  // WhatsApp share handler for PO
  const handleWhatsAppShare = async (poId) => {
    setLoading(true);
    setLoadingMessage('Preparing WhatsApp share...');

    try {
      const freshData = await fetchSinglePurchaseOrder(poId);
      if (!freshData) {
        setError('Failed to fetch purchase order details for sharing.');
        setLoading(false);
        return;
      }

      const poData = freshData.PurchaseOrder || freshData;
      const details = freshData.PurchaseOrderDetails || freshData.Details || [];
      const termsDetails = freshData.PurchaseTermsDetails || [];

      let supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId);

      // Fetch full supplier details to get TradeLicenseNo and other fields
      if (poData.MasterId) {
        try {
          const supplierResponse = await fetchData('ClientAndVendorApi', `/ClientAndVendorDetails/${poData.MasterId}`, token);
          const supplierData = supplierResponse.response_data?.Ledger || supplierResponse.response_data || supplierResponse || {};
          supplier = { ...supplier, ...supplierData };
        } catch (err) {
          console.error('Error fetching supplier details:', err);
        }
      }

      // Determine if International or Local PO
      const currencyName = poData.CurrencyName || supplier?.CurrencyName || '';
      const isInternationalPO = currencyName && !['aed', 'dirham', 'uae dirham'].includes(currencyName.toLowerCase().trim());

      // Fetch branch details
      let branchDetails = { branchName: '', location: '', addressLine1: '', addressLine2: '', pincode: '', companyName: '', gstNumber: '', iecCode: '', tradeLicenseNo: '' };
      try {
        const branchResponse = await fetchData('BranchApi', `/BranchDetails/${branchId}`, token);
        if (branchResponse.responseCode === '1111' || branchResponse.response_data) {
          const branchData = branchResponse.response_data || branchResponse || {};
          branchDetails = {
            branchName: branchData.Name || '',
            location: branchData.Location || '',
            addressLine1: branchData.AddressLine1 || '',
            addressLine2: branchData.AddressLine2 || '',
            pincode: branchData.PinCode || '',
            companyName: branchData.CompanyName || branchData.Name || '',
            gstNumber: branchData.GSTNumber || branchData.VATNumber || branchData.CompanyGSTNumber || '',
            iecCode: branchData.IECCode || branchData.IecCode || branchData.ImporterCode || '',
            tradeLicenseNo: branchData.TradeLicenseNo || branchData.TradeLicense || ''
          };
        }
      } catch (err) {
        console.error('Error fetching branch details:', err);
      }

      const supplierPhone = supplier?.ContactNumber1 || supplier?.ContactNumber || supplier?.Mobile || '';
      let result;

      if (isInternationalPO) {
        // Get Country of Origin for international
        let countryOfOrigin = supplier?.CountryName || supplier?.Country || '';
        if (!countryOfOrigin && supplier?.CountryId) {
          try {
            const countryResponse = await fetchData('CountryApi', `/${companyId}`, token);
            const countriesList = countryResponse.response_data || countryResponse || [];
            const matchedCountry = countriesList.find(c => c.Id === supplier.CountryId);
            countryOfOrigin = matchedCountry?.CountryName || matchedCountry?.Name || '';
          } catch (err) {
            console.error('Error fetching country:', err);
          }
        }
        result = await shareInternationalPOViaWhatsApp(poData, details, supplier, branchDetails, countryOfOrigin, termsDetails, supplierPhone);
      } else {
        result = await shareLocalPOViaWhatsApp(poData, details, supplier, branchDetails, termsDetails, supplierPhone);
      }

      if (result.success) {
        if (result.message) {
          setSuccess(result.message);
        } else {
          setSuccess('Shared via WhatsApp successfully!');
        }
      } else {
        setError(result.error || 'Failed to share via WhatsApp');
      }
    } catch (error) {
      console.error('Error sharing via WhatsApp:', error);
      setError('Failed to share via WhatsApp.');
    }

    setLoading(false);
  };

  // Memoized table row component for better performance
  const PurchaseOrderRow = useMemo(() => {
    // return ({ item, index, startIndex, supplierLookup, handleOpenModal, handleSupplierClick, handleOpenDeleteModal, handlePrintPO, handleOpenInactiveModal }) => {
    return ({ item, index, startIndex, supplierLookup, handleOpenModal, handleSupplierClick, handleOpenDeleteModal, handlePrintPO, handleDownloadPDF }) => {
      const supplier = supplierLookup[item.MasterId || item.SupplierId];
      const supplierName = item.MasterName || supplier?.Name || '-';
      const currencyName = item.CurrencyName || supplier?.CurrencyName || '';

      return (
        <tr key={item.Id}>
          <td>
            <span onClick={() => handleOpenModal('edit', item)} style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
              {startIndex + index + 1}
            </span>
          </td>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShoppingCart sx={{ fontSize: 18, color: '#1e40af' }} />
              {item.PurchaseOrderNo || '-'}
            </div>
          </td>
          <td>{formatDate(item.PurchaseOrderDate)}</td>
          <td>
            <span
              onClick={() => handleSupplierClick(item.MasterId || item.SupplierId, supplierName)}
              style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
            >
              {supplierName}
            </span>
          </td>
          <td>{supplier?.IECCode || '-'}</td>
          <td style={{ fontWeight: 600 }}>{formatCurrency(item.TotalAmount, currencyName)}</td>
          <td>
            {item.IsVerified ? (
              <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
            ) : (
              <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
            )}
          </td>
          <td>
            {item.IsConverted ? (
              <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            ) : (
              <Chip label="Open" color="default" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            )}
          </td>
          <td style={{ textAlign: 'right' }}>
            <div className="action-buttons">
              <Tooltip title="View">
                <IconButton size="small" onClick={() => handleOpenModal('view', item.Id)}>
                  <Visibility style={{ color: 'white' }} />
                </IconButton>
              </Tooltip>
              {canEditTransaction && (
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => handleOpenModal('edit', item.Id)} disabled={item.IsVerified || item.IsConverted}>
                    <Edit style={{ color: 'white' }} />
                  </IconButton>
                </Tooltip>
              )}
              {canDeleteTransaction && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleOpenDeleteModal(item.Id, item.PurchaseOrderNo)} disabled={item.IsVerified || item.IsConverted}>
                    <Delete style={{ color: 'white' }} />
                  </IconButton>
                </Tooltip>
              )}
              {item.IsVerified && (
                <Tooltip title="Print">
                  <IconButton size="small" onClick={() => handlePrintPO(item.Id)} className="print-action-btn">
                    <Print style={{ color: 'white' }} />
                  </IconButton>
                </Tooltip>
              )}
              {item.IsVerified && (
                <Tooltip title="Download PDF">
                  <IconButton size="small" onClick={() => handleDownloadPDF(item.Id)} sx={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', ml: 0.5, '&:hover': { background: 'linear-gradient(135deg, #b91c1c, #991b1b)' } }}>
                    <PictureAsPdf style={{ color: 'white', fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              {item.IsVerified && (
                <Tooltip title="Share WhatsApp">
                  <IconButton size="small" onClick={() => handleWhatsAppShare(item.Id)} sx={{ background: '#25D366', color: 'white', ml: 0.5, '&:hover': { background: '#1da851' } }}>
                    <WhatsApp style={{ color: 'white', fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </td>
        </tr>
      );
    };
  }, [canEditTransaction, canDeleteTransaction]);

  // Format helpers - memoized
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, []);

  const formatCurrency = useCallback((amount, currencyName) => {
    const getSymbol = (name) => {
      if (!name) return '₹';
      const n = name.toLowerCase().trim();
      const symbolMap = {
        'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
        'usd': '$', 'dollar': '$', 'us dollar': '$',
        'eur': '€', 'euro': '€',
        'gbp': '£', 'pound': '£', 'british pound': '£',
        'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
        'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
        'jpy': '¥', 'yen': '¥',
        'cny': '¥', 'yuan': '¥', 'rmb': '¥',
        'aud': 'A$', 'australian dollar': 'A$',
        'cad': 'C$', 'canadian dollar': 'C$',
        'sgd': 'S$', 'singapore dollar': 'S$',
        'chf': 'CHF', 'swiss franc': 'CHF',
        'myr': 'RM', 'ringgit': 'RM',
        'thb': '฿', 'baht': '฿',
        'krw': '₩', 'won': '₩',
        'bdt': '৳', 'taka': '৳',
        'lkr': 'Rs', 'sri lankan rupee': 'Rs',
        'pkr': '₨', 'pakistani rupee': '₨',
        'npr': 'रू', 'nepalese rupee': 'रू',
        'zar': 'R', 'rand': 'R',
        'rub': '₽', 'ruble': '₽',
        'try': '₺', 'lira': '₺',
        'idr': 'Rp', 'rupiah': 'Rp',
        'php': '₱', 'peso': '₱',
        'vnd': '₫', 'dong': '₫',
        'ngn': '₦', 'naira': '₦',
        'egp': 'E£', 'egyptian pound': 'E£',
        'qar': 'QR', 'qatari riyal': 'QR',
        'kwd': 'KD', 'kuwaiti dinar': 'KD',
        'bhd': 'BD', 'bahraini dinar': 'BD',
        'omr': 'OMR', 'omani rial': 'OMR'
      };
      if (symbolMap[n]) return symbolMap[n];
      for (const [key, symbol] of Object.entries(symbolMap)) {
        if (n.includes(key)) return symbol;
      }
      return name;
    };
    const symbol = getSymbol(currencyName);
    return `${symbol}${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const { data: paginatedData, totalCount, totalPages, startIndex } = paginationData;


  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Romans',
      }}>
        {loading && <LoadingOverlay message={loadingMessage} />}

        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ShoppingCart sx={{ fontSize: 24, color: '#0f3460' }} />
            <h4 style={{
              fontSize: '17px',
              fontWeight: '700',
              margin: 0,
              letterSpacing: '-0.3px',
              fontFamily: 'Times New Romans',
              textTransform: 'uppercase',
              color: 'black'
            }}>
              Purchase Order Management
            </h4>
          </div>
        </div>

        {/* Date Filters, Search and Create Button Row */}
        <div style={{
          marginBottom: '12px',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin'
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-end',
            minWidth: 'fit-content',
            paddingBottom: '4px'
          }}>
            {/* From Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); fetchPurchaseOrders(e.target.value, toDate); }}
                sx={{
                  width: isMobile ? '120px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* To Date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); fetchPurchaseOrders(fromDate, e.target.value); }}
                sx={{
                  width: isMobile ? '120px' : '140px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            </div>

            {/* Search */}
            {isMobile ? (
              searchExpanded ? (
                <TextField
                  size="small"
                  placeholder="Search by PO No, Supplier..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  autoFocus
                  onBlur={() => { if (!searchTerm) setSearchExpanded(false); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: '200px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '12px', height: '32px',
                      '&:hover fieldset': { borderColor: '#0f3460' },
                      '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                    },
                  }}
                />
              ) : (
                <Tooltip title="Search" arrow>
                  <Button
                    onClick={() => setSearchExpanded(true)}
                    style={{ minWidth: '32px', width: '32px', height: '32px', padding: '0', borderRadius: '6px', backgroundColor: '#f8f9fa', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                  </Button>
                </Tooltip>
              )
            ) : (
              <TextField
                size="small"
                placeholder="Search by PO No, Supplier..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search style={{ color: '#0f3460', fontSize: '18px' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: '250px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px', backgroundColor: '#f8f9fa', fontSize: '12px', height: '32px',
                    '&:hover fieldset': { borderColor: '#0f3460' },
                    '&.Mui-focused fieldset': { borderColor: '#0f3460' },
                  },
                }}
              />
            )}

            {/* Create PO Button */}
            {canCreateTransaction && (
              <Tooltip title="Create a new purchase order" arrow>
                <Button
                  variant="contained"
                  onClick={() => handleOpenModal('create')}
                  style={{
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                    color: 'white',
                    padding: isMobile ? '0' : '4px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '700',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                    border: 'none',
                    height: '32px',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    whiteSpace: 'nowrap',
                    minWidth: isMobile ? '32px' : '100px',
                    width: 'auto',
                    marginLeft: 'auto'
                  }}
                >
                  <Add style={{ fontSize: '18px' }} />
                  {!isMobile && <span style={{ fontSize: '12px' }}>Create</span>}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <Table sx={{ minWidth: 650 }} aria-label="purchase order table">
              <TableHead>
                <TableRow style={{
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                  height: '27px'
                }}>
                  {['Sl No.', 'PO No.', 'Date', 'Supplier', 'Total Amount', 'Verified', 'Converted', 'Actions'].map((header, idx) => (
                    <TableCell key={header} style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '4px 12px',
                      textAlign: idx === 0 ? 'center' : idx >= 4 ? 'right' : 'left',
                      height: '27px'
                    }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? paginatedData.map((item, index) => {
                  const supplier = supplierLookup[item.MasterId || item.SupplierId];
                  const supplierName = item.MasterName || supplier?.Name || '-';
                  const currencyName = item.CurrencyName || supplier?.CurrencyName || '';
                  return (
                    <TableRow key={item.Id} style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>
                        {startIndex + index + 1}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ShoppingCart sx={{ fontSize: 18, color: '#0f3460' }} />
                          {item.PurchaseOrderNo || '-'}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.PurchaseOrderDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        <span
                          onClick={() => handleSupplierClick(item.MasterId || item.SupplierId, supplierName)}
                          style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
                        >
                          {supplierName}
                        </span>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>
                        {formatCurrency(item.TotalAmount, currencyName)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        {item.IsVerified ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        ) : (
                          <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        )}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        {item.IsConverted ? (
                          <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        ) : (
                          <Chip label="Open" color="default" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                        )}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Tooltip title="View Details" arrow>
                            <Button size="small" onClick={() => handleOpenModal('view', item.Id)}
                              style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', fontSize: '14px', background: 'none' }}>
                              👁️
                            </Button>
                          </Tooltip>
                          {canEditTransaction && (
                            <Tooltip title="Edit" arrow>
                              <Button size="small" onClick={() => handleOpenModal('edit', item.Id)} disabled={item.IsVerified || item.IsConverted}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', fontSize: '14px', background: 'none' }}>
                                ✏️
                              </Button>
                            </Tooltip>
                          )}
                          {canDeleteTransaction && (
                            <Tooltip title="Delete" arrow>
                              <Button size="small" onClick={() => handleOpenDeleteModal(item.Id, item.PurchaseOrderNo)} disabled={item.IsVerified || item.IsConverted}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', fontSize: '14px', background: 'none' }}>
                                🗑️
                              </Button>
                            </Tooltip>
                          )}
                          {item.IsVerified && (
                            <Tooltip title="Print" arrow>
                              <Button size="small" onClick={() => handlePrintPO(item.Id)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', fontSize: '14px', background: 'none' }}>
                                🖨️
                              </Button>
                            </Tooltip>
                          )}
                          {item.IsVerified && (
                            <Tooltip title="Download PDF" arrow>
                              <Button size="small" onClick={() => handleDownloadPDF(item.Id)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', fontSize: '14px', background: 'none' }}>
                                📄
                              </Button>
                            </Tooltip>
                          )}
                          {item.IsVerified && (
                            <Tooltip title="Share WhatsApp" arrow>
                              <Button size="small" onClick={() => handleWhatsAppShare(item.Id)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', border: 'none', background: 'none' }}>
                                <WhatsApp sx={{ fontSize: 16, color: '#25D366' }} />
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      No purchase orders found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <div className="pagination-section">
          <div className="pagination-info">
            <span>Showing {totalCount > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount} entries</span>
            <div className="rows-per-page">
              <span>Rows per page:</span>
              <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          <div className="pagination-controls">
            <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
            <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
            {[...Array(totalPages)].map((_, i) => {
              const page = i + 1;
              if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                return (
                  <button key={page} className={`pagination-btn ${currentPage === page ? 'active' : ''}`} onClick={() => handlePageChange(page)}>
                    {page}
                  </button>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <span key={page} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}
            <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>Next</button>
            <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>Last</button>
          </div>
        </div>
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} />

      <PurchaseOrderModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        onSave={handleSave}
        onVerify={handleVerify}
        onConvert={handleConvert}
        suppliers={suppliers}
        setSuppliers={setSuppliers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
        onFetchPOHistory={fetchPOHistoryForModal}
        onFetchItems={fetchItems}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Purchase Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete purchase order <strong style={{ color: '#1e293b' }}>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Purchase Orders Modal */}
      <Dialog
        open={supplierModalOpen}
        onClose={handleCloseSupplierModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Purchase Orders - {selectedSupplier?.name}
              </Typography>
              <Chip
                label={`${supplierPurchaseOrders.length} Orders`}
                color="primary"
                size="small"
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            </Box>
            <IconButton onClick={handleCloseSupplierModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            {supplierPurchaseOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
                <Typography>No purchase orders found for this supplier</Typography>
              </Box>
            ) : (
              supplierPurchaseOrders.map((po, poIndex) => (
                <Box key={po.Id} sx={{ borderBottom: '1px solid #e2e8f0' }}>
                  {/* PO Header Row - Clickable */}
                  <Box
                    onClick={() => togglePOExpand(po.Id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      cursor: 'pointer',
                      bgcolor: expandedPOId === po.Id ? '#f0f9ff' : (poIndex % 2 === 0 ? '#fff' : '#f9fafb'),
                      '&:hover': { bgcolor: '#f0f9ff' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {expandedPOId === po.Id ? <ExpandLess sx={{ color: '#1e40af' }} /> : <ExpandMore sx={{ color: '#64748b' }} />}
                        <Typography sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.9rem' }}>
                          {po.PurchaseOrderNo}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                        {formatDate(po.PurchaseOrderDate)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {po.IsVerified ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 12 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        ) : (
                          <Chip label="Pending" color="warning" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        )}
                        {po.IsConverted && (
                          <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#1e40af', fontSize: '0.95rem' }}>
                      {formatCurrency(po.TotalAmount, po.CurrencyName || supplierLookup[selectedSupplier?.id]?.CurrencyName)}
                    </Typography>
                  </Box>

                  {/* PO Details - Expandable */}
                  <Collapse in={expandedPOId === po.Id}>
                    <Box sx={{ bgcolor: '#f8fafc', p: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', mb: 1, display: 'block' }}>
                        Order Details ({po.PurchaseOrderDetails?.length || 0} items)
                      </Typography>
                      <Box sx={{ overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                          <thead>
                            <tr style={{ background: '#e2e8f0' }}>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>#</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Item</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>HSN</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Qty</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Rate</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Amount</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>GST%</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(po.PurchaseOrderDetails || []).map((detail, detailIndex) => (
                              <tr key={detail.Id || detailIndex} style={{ borderBottom: '1px solid #e2e8f0', background: detailIndex % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                <td style={{ padding: '8px', textAlign: 'left' }}>{detailIndex + 1}</td>
                                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 500 }}>{detail.ItemName || '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{detail.Quantity || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Rate, po.CurrencyName || supplierLookup[selectedSupplier?.id]?.CurrencyName)}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Amount, po.CurrencyName || supplierLookup[selectedSupplier?.id]?.CurrencyName)}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{detail.VATRate || 0}%</td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount, po.CurrencyName || supplierLookup[selectedSupplier?.id]?.CurrencyName)}</td>
                              </tr>
                            ))}
                            {(!po.PurchaseOrderDetails || po.PurchaseOrderDetails.length === 0) && (
                              <tr>
                                <td colSpan="8" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No details available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Box>
                      {/* Summary Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1.5, pt: 1, borderTop: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Taxable: <strong style={{ color: '#374151' }}>{formatCurrency(po.TaxableAmount, po.CurrencyName || supplierLookup[selectedSupplier?.id]?.CurrencyName)}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          VAT: <strong style={{ color: '#166534' }}>{formatCurrency(po.VATAmount || 0, po.CurrencyName || supplierLookup[selectedSupplier?.id]?.CurrencyName)}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Total: <strong style={{ color: '#1e40af' }}>{formatCurrency(po.TotalAmount, po.CurrencyName || supplierLookup[selectedSupplier?.id]?.CurrencyName)}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Total Orders: <strong>{supplierPurchaseOrders.length}</strong> |
            Total Amount: <strong style={{ color: '#1e40af' }}>{(supplierPurchaseOrders.reduce((sum, po) => sum + (po.TotalAmount || 0), 0), supplierLookup[selectedSupplier?.id]?.CurrencyName)}</strong>
          </Typography>
          <Button onClick={handleCloseSupplierModal} variant="outlined" size="small" sx={{ borderColor: '#d1d5db', color: '#374151' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default PurchaseOrderIndex;
